import { FC } from "react";
import { Dialog } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import Button from "components/Button";
import { getScheduleDisplayName } from "pages/crawler/utils/crawlerFormUtils";
import { getOutputFormatDisplayText } from "pages/crawler/utils/outputFormatUtils";

interface CrawlerSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  formValues: any;
  crawlerName: string;
  confirmButtonText?: string;
}

const CrawlerSummaryModal: FC<CrawlerSummaryModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  formValues,
  crawlerName,
  confirmButtonText = "SCHEDULE JOB",
}) => {
  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <Dialog.Title className="text-lg font-medium text-gray-900">
              Review Crawler Configuration
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-6">
              {/* Crawler Name */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Crawler Name
                </h3>
                <p className="text-sm text-gray-900">{crawlerName}</p>
              </div>

              {/* URL to Crawl */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  URL to Crawl
                </h3>
                <p className="text-sm text-gray-900 break-all">{formValues.url}</p>
              </div>

              {/* Crawler Options */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Crawler Options
                </h3>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Link Depth</dt>
                    <dd className="text-sm text-gray-900">{formValues.linkDepth || "Not set"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Maximum Cost</dt>
                    <dd className="text-sm text-gray-900">{formValues.maxCost || "Not set"}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm font-medium text-gray-700 mb-1">Include Path</dt>
                    <dd className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded break-all">{formValues.includePath}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm font-medium text-gray-700 mb-1">Exclude Path</dt>
                    <dd className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded break-all">{formValues.excludePath || "None"}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm font-medium text-gray-700 mb-1">Callback URL</dt>
                    <dd className="text-sm text-gray-900 break-all">{formValues.callbackUrl}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Crawling frequency</dt>
                    <dd className="text-sm text-gray-900">
                      {getScheduleDisplayName(formValues.schedule)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Output Format</dt>
                    <dd className="text-sm text-gray-900">{getOutputFormatDisplayText(formValues.outputFormat, formValues.autoparse)}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-sm font-medium text-gray-700 mb-1">Notification</dt>
                    <dd className="text-sm text-gray-900 capitalize">{formValues.notification}</dd>
                  </div>
                </dl>
              </div>

              {/* Advanced Options - Always show */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Advanced Options
                </h3>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Disable Redirects</dt>
                    <dd className="text-sm text-gray-900">{formValues.disableRedirects ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Retry 404</dt>
                    <dd className="text-sm text-gray-900">{formValues.retry404 ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Render JS</dt>
                    <dd className="text-sm text-gray-900">{formValues.renderJs ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Use Own Headers</dt>
                    <dd className="text-sm text-gray-900">{formValues.useOwnHeaders ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Advanced Bypassing</dt>
                    <dd className="text-sm text-gray-900">{formValues.advancedBypassing ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Residential IPs</dt>
                    <dd className="text-sm text-gray-900">{formValues.residentialIps ? "Yes" : "No"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Session Number</dt>
                    <dd className="text-sm text-gray-900">{formValues.sessionNumber !== undefined ? formValues.sessionNumber : 'Not set'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Country Code</dt>
                    <dd className="text-sm text-gray-900">{formValues.countryCode || "US"}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-700 mb-1">Device Type</dt>
                    <dd className="text-sm text-gray-900">{formValues.deviceType || "Desktop"}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t">
            <Button
              text="BACK TO EDIT"
              className="button button-secondary rounded"
              onClick={onClose}
            />
            <Button
              text={confirmButtonText}
              className="button button-primary rounded"
              onClick={onConfirm}
            />
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default CrawlerSummaryModal;
